/**
 * Consolidated constants exports
 *
 * This module provides a unified entry point for all application constants,
 * organized by domain for better discoverability and maintainability.
 *
 * @example
 * // Domain-organized imports (recommended for new code)
 * import { CLINIC_CONSTANTS } from '@/lib/constants'
 * const inactiveDays = CLINIC_CONSTANTS.INACTIVE_CASES_DAYS
 *
 * // Direct imports (backward compatible)
 * import { FREQUENCY_OPTIONS } from '@/lib/constants'
 */

// =============================================================================
// CLINIC CONSTANTS
// =============================================================================

/**
 * Clinical and medical-related constants including patient care thresholds,
 * default fees, and clinical workflow settings.
 */
export const CLINIC_CONSTANTS = {
  /** Number of days after which a patient case is considered inactive */
  INACTIVE_CASES_DAYS: 14,

  /** Default Outpatient Department (OPD) consultation fee */
  OPD_FEE_DEFAULT: 2000,
} as const

// =============================================================================
// PAGINATION CONSTANTS
// =============================================================================

/**
 * Pagination and list display constants.
 */
export const PAGINATION = {
  /** Default number of items per page */
  DEFAULT_PAGE_SIZE: 10,

  /** Page size options for user selection */
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],

  /** Maximum items to show in dropdown selects */
  MAX_SELECT_ITEMS: 100,
} as const

// =============================================================================
// UI CONSTANTS
// =============================================================================

/**
 * UI-related constants for timeouts, animations, and display settings.
 */
export { NETWORK_CONSTANTS } from './network'

export { PRODUCT_DESCRIPTION, PRODUCT_NAME } from './brand'

export const UI_CONSTANTS = {
  /** Default toast notification duration in milliseconds */
  TOAST_DURATION: 3000,

  /** Error toast notification duration in milliseconds */
  ERROR_TOAST_DURATION: 5000,

  /** Debounce delay for search inputs in milliseconds */
  SEARCH_DEBOUNCE_MS: 300,

  /** Stale time for cached data in milliseconds (5 minutes) */
  DEFAULT_STALE_TIME: 5 * 60 * 1000,
} as const

// =============================================================================
// DATE CONSTANTS
// =============================================================================

/**
 * Date and time-related constants.
 */
export const DATE_CONSTANTS = {
  /** ISO date format string (YYYY-MM-DD) */
  ISO_DATE_FORMAT: 'YYYY-MM-DD',

  /** Display date format */
  DISPLAY_DATE_FORMAT: 'MMM DD, YYYY',

  /** Display datetime format */
  DISPLAY_DATETIME_FORMAT: 'MMM DD, YYYY h:mm A',
} as const

// =============================================================================
// RE-EXPORTS FROM DOMAIN MODULES
// =============================================================================

// Dosage frequencies - kept in separate module due to size
export {
  FREQUENCY_OPTIONS,
  getDosesPerDay,
  type DosageFrequencyOption,
} from './dosageFrequencies'

export {
  DOSAGE_MULTIPLIER_OPTIONS,
  type DosageMultiplierOption,
} from './prescription'

// Consultation recommendations - clinical decision support
export {
  BUILTIN_CONSULTATION_RECOMMENDATION_CODES,
  DEFAULT_CONSULTATION_RECOMMENDATIONS,
} from './consultationRecommendations'

// Drug units - dispensary inventory
export {
  DRUG_UNIT_OPTIONS,
  DRUG_UNIT_CUSTOM_VALUE,
  OCULAR_DRUG_UNITS,
  LEGACY_OCULAR_DRUG_UNITS,
  isOcularDrugUnit,
  defaultEyeForDrugUnit,
} from './drugUnits'
export type { PrescriptionEyeDefault } from './drugUnits'

// =============================================================================
// LEGACY EXPORTS (for backward compatibility during migration)
// =============================================================================

/** @deprecated Use CLINIC_CONSTANTS.INACTIVE_CASES_DAYS instead */
export const INACTIVE_CASES_DAYS = CLINIC_CONSTANTS.INACTIVE_CASES_DAYS

/** @deprecated Use CLINIC_CONSTANTS.OPD_FEE_DEFAULT instead */
export const OPD_FEE_DEFAULT = CLINIC_CONSTANTS.OPD_FEE_DEFAULT
